// libraries
import type { FeatureToggles } from '@makemydeal/dr-platform-shared';
import type {
    CoBuyer,
    CreditDecisionDetails,
    CreditScoreSource,
    IPencilOfferInputs,
    Offer,
    PaymentTerm
} from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, LEASE, OFFER_ENCRYPTION_FIELDS, OFFER_STATE_SIGNATURE } from '@makemydeal/dr-platform-types';
import { CALC_ENDPOINTS } from '../constants/state';

export type OfferReduxInitialState = Offer & {
    toggles?: FeatureToggles;
};

export const initialState: OfferReduxInitialState = {
    [OFFER_STATE_SIGNATURE]: true,
    [OFFER_ENCRYPTION_FIELDS.PAYMENT]: '',
    accepted: false,
    applicationName: '',
    cashApplyMethod: null as unknown as string | undefined,
    customRateGroup: null,
    calculationEndpoints: {
        currentCalculationEndpoint: CALC_ENDPOINTS.programQuotes,
        initialCalculationEndpoint: CALC_ENDPOINTS.programQuotes,
        calculationEndpointByOfferType: {
            [FINANCE]: CALC_ENDPOINTS.programQuotes,
            [LEASE]: CALC_ENDPOINTS.programQuotes,
            [BALLOON]: CALC_ENDPOINTS.programQuotes
        }
    },
    commonOrgId: '',
    commonConsumerId: '',
    cmdMessageToShopper: '',
    cmdMessageToSalesperson: '',
    coBuyer: null as unknown as CoBuyer | undefined,
    createdOn: '',
    creditAppId: '',
    creditTiers: {
        byId: {
            /*index by id*/
        },
        allIds: [
            /*array of ids*/
        ],
        byFinance: [
            /*array of ids*/
        ],
        byLease: [
            /*array of ids*/
        ],
        byBalloon: [
            /*array of ids*/
        ]
    },
    creditApp: {
        creditAppReferenceId: '',
        accelerateCreditAppId: '',
        shouldApplyDecisionDetails: false,
        decisionDetails: {} as CreditDecisionDetails
    },
    creditScore: {
        description: '',
        high: 0,
        low: 0
    },
    creditScoreSource: null as unknown as CreditScoreSource | undefined,
    dealLifecycle: {},
    dealRefId: '',
    deal_ref_id: '',
    dealerUser: '',
    dealXgId: '',
    dealXgDetails: {},
    dealXgVersion: '',
    deviceType: '',
    featureToggles: {},
    fsDealRefId: '',
    globalCustomerId: '',
    incentives: {
        byId: {
            /*index by id*/
        },
        allIds: [
            /*array of ids*/
        ],
        byOfferType: {
            finance: {
                conditionals: [
                    /*array of ids*/
                ],
                nonConditionals: [
                    /*array of ids*/
                ]
            },
            lease: {
                conditionals: [
                    /*array of ids*/
                ],
                nonConditionals: [
                    /*array of ids*/
                ]
            },
            cash: {
                conditionals: [
                    /*array of ids*/
                ],
                nonConditionals: [
                    /*array of ids*/
                ]
            },
            balloon: {
                conditionals: [
                    /*array of ids*/
                ],
                nonConditionals: [
                    /*array of ids*/
                ]
            }
        },
        mappings: {}
    },
    incompleteDealDetails: {},
    initialized: false,
    isTargetOutOfPocket: false,
    leaseAnnualMiles: [],
    leaseMileage: 0,
    message: '',
    monthlyPayment: 0,
    monthlyPaymentWithoutProducts: 0,
    shiftSessionId: '',
    udCorrelationId: '',
    shopper: {
        firstName: '',
        lastName: '',
        zip: '',
        phone: '',
        email: '',
        contactPreferences: {
            email: false,
            phone: false,
            sms: false
        },
        streetAddress: '',
        state: '',
        city: ''
    },
    shouldDisplayZipModal: false,
    offerType: FINANCE,
    offerSource: null as unknown as string | undefined,
    payment: {} as PaymentTerm,
    paymentTermsFallback: {
        fields: [],
        hasMessageBeenShown: false
    },
    penciledInputs: {
        byOfferType: {
            finance: {} as IPencilOfferInputs,
            lease: {} as IPencilOfferInputs,
            cash: {} as IPencilOfferInputs,
            balloon: {} as IPencilOfferInputs
        },
        allTypes: [
            /*array of ids*/
        ],
        savedInputs: {
            offerType: FINANCE
        }
    },
    previousTerms: {},
    sessionId: '',
    settings: {
        baseLeaseOnMSRP: false,
        ddcAccountId: '',
        defaultOfferType: FINANCE,
        isCashEnabled: false,
        isLeaseEnabled: false,
        isNewSinglePrice: false,
        isUsedSinglePrice: false,
        isDueAtSigninAmountDeduct: false
    },
    source: {
        listingCode: '',
        route: null,
        sponsor: '',
        vdpUrl: ''
    },
    terms: {
        byId: {
            /*index by id*/
        },
        allIds: [
            /*array of ids*/
        ],
        financeTerms: [
            /*array of ids*/
        ],
        leaseTerms: [
            /*array of ids*/
        ],
        balloonTerms: [
            /*array of ids*/
        ]
    },
    toggles: {},
    totalFinanced: 0,
    vehicle: {
        vin: '',
        listedPrice: 0,
        retailPrice: 0,
        condition: '',
        mileage: 0,
        year: 0,
        make: '',
        model: '',
        trim: '',
        imageUrl: '',
        stockNumber: '',
        styleId: '',
        type: '',
        interiorColor: '',
        exteriorColor: '',
        preference: '',
        imageTag: '',
        options: [],
        vehicleId: '',
        fuel: '',
        bodyStyle: '',
        inventoryDate: ''
    },
    vehicleInsurance: {},
    walletId: '',
    template: {
        isTemplateApplied: false,
        name: '',
        withPaymentTerms: false
    },
    vehicleFulfillment: {},
    participantsSPD: []
};
